/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

// import Link from 'next/link'
import Image from "next/image";

import HeaderSearchPanel from "../HeaderSearchPanel";

import Arrow from "components/Shared/icons/arrow";
import SearchIcon from "components/Shared/icons/search";

import { CleanRelativeLink } from "components/Shared/DomainSplit";
import { isBlankTarget, relForBlankTarget } from "lib/linkExternalProps";
import { ariaLabelWithNewWindowNotice, newWindowNoticeTitle } from "lib/newWindowA11y";

import LanguagesDropdown_DGA from "./Components/languages-dropdown_DGA/LanguagesDropdown_DGA";
import { ExternalLinkTypeIcon } from "components/Shared/dga-link/ExternalLinkTypeIcon";
import { isHeaderNavLinkHidden } from "./helpers/headerNav_DGAHelpers";

interface DesktopNavProps {
  activeNavItem: any;
  setActiveNavItem: React.Dispatch<React.SetStateAction<any>>;
  isSearchPanelOpen: boolean;
  setIsSearchPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fields?: any;
  locale?: string;
  Languages: any[];
  unSupportedLocales: string[];
  selectedHeaderNavItemsIds: any;
}

const DesktopNav: React.FC<DesktopNavProps> = (props) => {
  const {
    activeNavItem,
    setActiveNavItem,
    isSearchPanelOpen,
    setIsSearchPanelOpen,
    locale,
    unSupportedLocales,
    selectedHeaderNavItemsIds,
  } = props || {};


  // local clicked selection state: used so a click marks an item as selected visually
  const [clickedItemId, setClickedItemId] = React.useState<string | null>(null);

  // Clear clickedItemId when submenu closes (activeNavItem is cleared)
  React.useEffect(() => {
    // If activeNavItem is empty or doesn't have SubMenuSection, clear clickedItemId
    if (!activeNavItem?.fields?.SubMenuSection?.length) {
      setClickedItemId(null);
    }
  }, [activeNavItem]);

  const handleClickOnSearchIcon = () => {
    if (activeNavItem?.fields?.SubMenuSection?.length > 0) {
      setActiveNavItem({});
      setIsSearchPanelOpen(true);
    } else {
      setIsSearchPanelOpen((prev) => !prev);
    }
  };

  const handleClickOnNavItem = (menuItem: any) => {
    // If clicking the same item, do nothing
    if (activeNavItem?.fields?.["Link Text"]?.value === menuItem?.fields?.["Link Text"]?.value)
      return;

    // Clear previous clicked item when clicking a different item
    if (clickedItemId && clickedItemId !== menuItem?.id) {
      setClickedItemId(null);
    }

    setIsSearchPanelOpen(false);

    setActiveNavItem(() => {
      if (menuItem?.fields?.SubMenuSection?.length > 0) {
        return menuItem;
      }
    });
  };

  // const [isLangsDDLOpen, setIsLangsDDLOpen] = React.useState<boolean>(false);

  const langs = props?.fields?.Languages?.map((lang: any) => ({
    label: lang?.fields?.Title?.value,
    href: lang?.fields?.Code?.fields?.Iso?.value,
    unsupportedText: lang?.fields?.UnsupportedText?.value || "",
  }));

  const subMenuOpen = (activeNavItem?.fields?.SubMenuSection?.length ?? 0) > 0;

  return (
    <>
      <div className="flex min-h-[72px] items-center justify-between w-full relative z-[100] bg-white">
        {/* logo and navigation */}
        <div className="flex items-center gap-4">
          {/* logo */}
          <a
            href={locale ? `/${locale}` : "/"}
            className="flex w-[93px] h-[59px] items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#161616] focus-visible:ring-offset-2"
            aria-label={
              locale?.toLowerCase() === "ar"
                ? "الصفحة الرئيسية لوزارة الثقافة"
                : locale?.toLowerCase() === "zh"
                  ? "沙特阿拉伯文化部主页"
                  : locale?.toLowerCase() === "ko"
                    ? "사우디아라비아 문화부 홈"
                    : "Saudi Ministry of Culture home"
            }
          >
            <Image
              loading="lazy"
              src={`/moc_logo_light.svg`}
              alt=""
              aria-hidden={true}
              width={93}
              height={60}
              className="cursor-pointer w-[93px] h-[59px] min-w-[93px] min-h-[59px] object-contain"
            />
          </a>

          {/* navigation menu items */}
          {props?.fields?.Links?.length > 0 && (
            <nav
              aria-label={
                locale?.toLowerCase() === "ar"
                  ? "التنقل الرئيسي"
                  : locale?.toLowerCase() === "zh"
                    ? "主导航"
                    : locale?.toLowerCase() === "ko"
                      ? "주 메뉴"
                      : "Primary navigation"
              }
            >
              <ul className="flex items-center gap-2">
                {props?.fields?.Links?.map(
                  (menuItem: any) => {
                    if (isHeaderNavLinkHidden(menuItem)) {
                      return null;
                    }

                    const hasSubmenu =
                      (menuItem?.fields?.SubMenuSection?.length ?? 0) > 0;
                    const isItemSelected =
                      selectedHeaderNavItemsIds.includes(menuItem?.id) ||
                      clickedItemId === menuItem?.id;
                    const isSubmenuOpen =
                      activeNavItem?.fields?.["Link Text"]?.value ===
                      menuItem?.fields?.["Link Text"]?.value;
                    const surfaceClassName = `text-black font-medium cursor-pointer relative whitespace-nowrap rounded hover:bg-[#F3F4F6] active:bg-[#E5E7EB] text-base transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#161616] focus-visible:ring-offset-2 focus:ring-2 focus:ring-[#161616] focus:ring-offset-2 ${isItemSelected
                        ? "bg-[#1B8354] hover:!bg-[#166A45] active:!bg-[#104631] !text-white focus-visible:ring-2 focus-visible:ring-[#161616]"
                        : ""
                      }`;

                    return (
                      (hasSubmenu || menuItem?.fields?.Link?.value?.href) && (
                        <li key={menuItem?.id} className="relative group">
                          {hasSubmenu ? (
                            menuItem?.fields?.["Link Text"]?.value && (
                              <button
                                type="button"
                                aria-expanded={isSubmenuOpen}
                                aria-haspopup="true"
                                aria-controls="dga-header-submenu-panel"
                                onClick={() => {
                                  handleClickOnNavItem(menuItem);
                                  setClickedItemId(menuItem?.id ?? null);
                                }}
                                className={surfaceClassName}
                              >
                                <span className="flex h-[72px] items-center gap-1 px-4">
                                  {menuItem?.fields?.["Link Text"]?.value}
                                  <Arrow
                                    aria-hidden={true}
                                    fill={isItemSelected ? "#ffffff" : "#161616"}
                                    className={`z-0 transition-transform origin-center ${isSubmenuOpen ? "-rotate-180" : "rotate-0"
                                      }`}
                                  />
                                </span>
                              </button>
                            )
                          ) : (
                            menuItem?.fields?.Link?.value?.href && (() => {
                              const leafTarget = (menuItem?.fields?.Link?.value?.target || "_self").trim() || "_self";
                              const leafBlank = isBlankTarget(menuItem?.fields?.Link?.value?.target);
                              const leafText = (menuItem?.fields?.["Link Text"]?.value || "").trim();
                              return (
                                <a
                                  href={
                                    CleanRelativeLink(menuItem?.fields?.Link?.value?.href) ||
                                    undefined
                                  }
                                  target={leafTarget}
                                  rel={relForBlankTarget(
                                    menuItem?.fields?.Link?.value?.target
                                  )}
                                  className={`${surfaceClassName} flex h-[72px] items-center px-4`}
                                  {...(leafBlank && leafText
                                    ? {
                                      "aria-label": ariaLabelWithNewWindowNotice(leafText, locale),
                                      title: newWindowNoticeTitle(locale),
                                    }
                                    : leafBlank
                                      ? { title: newWindowNoticeTitle(locale) }
                                      : {})}
                                >
                                  {menuItem?.fields?.["Link Text"]?.value}
                                </a>
                              );
                            })()
                          )}

                          <div
                            className={`absolute bottom-0 left-0 right-0 h-[6px] rounded-full z-30 transition-all w-[80%] mx-auto ${isItemSelected
                                ? "visible bg-[#54C08A] "
                                : "invisible group-hover:visible group-hover:bg-[#9DA4AE] group-active:bg-[#1F2A37]"
                              }`}
                          />
                        </li>
                      ));
                  })}
              </ul>
            </nav>
          )}
        </div>

        {/* search and language buttons */}
        <div className="flex items-center gap-2">
          {/* an array that includes both buttons, and their data */}
          {[
            {
              title:
                props?.locale?.toLowerCase() === "en"
                  ? "Search"
                  : props?.locale?.toLowerCase() === "zh"
                    ? "搜索"
                    : props?.locale?.toLowerCase() === "ko"
                      ? "검색"
                      : "بحث",
              icon: (
                <SearchIcon
                  color={isSearchPanelOpen ? "#FFFFFF" : "#161616"}
                  classes="stroke-current w-5 h-5 xl:w-6 xl:h-6"
                  aria-hidden={true}
                />
              ),
              onClick: handleClickOnSearchIcon,
            },
          ].map((buttonItem, index) => (
            // search and language buttons
            <button
              type="button"
              key={index}
              onClick={buttonItem?.onClick}
              className={`group relative flex h-[72px] items-center gap-1 rounded px-4 text-base font-medium transition-colors hover:bg-[#F3F4F6] active:bg-[#E5E7EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#161616] focus-visible:ring-offset-2 focus:ring-2 focus:ring-[#161616] focus:ring-offset-2 ${isSearchPanelOpen
                ? "bg-[#1B8354] hover:!bg-[#166A45] active:!bg-[#104631] text-white focus-visible:ring-2 focus-visible:ring-[#161616]"
                : "text-black"
                }`}
            >
              {buttonItem?.icon}
              {buttonItem?.title}
              {/* hover effect border bottom item */}
              <div
                className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-[6px] w-[86.89%] rounded-full z-30 transition-colors ${isSearchPanelOpen
                  ? "visible bg-[#54C08A]"
                  : "invisible group-hover:visible group-hover:bg-[#9DA4AE] group-active:bg-[#1F2A37]"
                  }`}
              />
            </button>
          ))}

          <LanguagesDropdown_DGA
            currentLocale={locale ?? "ar"}
            unSupportedLocales={unSupportedLocales}
            labels={{
              trigger:
                locale === "en"
                  ? "عربي"
                  : locale === "zh"
                    ? "عربي"
                    : locale === "ko"
                      ? "عربي"
                      : "English",
              options: langs,
            }}
          />
        </div>
      </div>

      {/*
        Submenu panel: id must stay in the DOM for aria-controls. Do not combine HTML hidden + Tailwind flex —
        flex wins in the cascade and leaves a visible white bar; use Tailwind hidden when closed instead.
      */}
      <div
        id="dga-header-submenu-panel"
        className={
          subMenuOpen
            ? "absolute top-full left-0 right-0 flex items-center justify-start py-6 w-full bg-white rounded-b-3xl z-[98] shadow-2xl animate-slide-in"
            : "hidden"
        }
        aria-hidden={!subMenuOpen}
      >
        {subMenuOpen && (
          <div
            className="responsive pl-24 rtl:pr-24 grid gap-6 w-full"
            style={{
              gridTemplateColumns: `repeat(${
                (activeNavItem?.fields?.SubMenuSection?.filter(
                  (item: any) => !isHeaderNavLinkHidden(item)
                )?.length || 1)
                }, min-content)`,
            }}
          >
            {/*
                        sub menu items
                        if an item has sub items, it is displayed as a heading with a list of its sub items,
                        otherwise it is displayed as a link
                    */}
            {activeNavItem?.fields?.SubMenuSection?.map((subItem: any) => {
              if (isHeaderNavLinkHidden(subItem)) {
                return null;
              }

              const visibleInnerItems =
                subItem?.fields?.InnerMenuSection?.filter(
                  (child: any) => !isHeaderNavLinkHidden(child)
                ) || [];

              return visibleInnerItems.length > 0 ? (
                <div
                  key={subItem?.id}
                  className="py-2 flex flex-col items-start gap-3 min-w-[154px] overflow-hidden"
                >
                  <h2 className="text-primary-800 px-3 xl:px-4 font-bold text-base xl:text-lg whitespace-nowrap">
                    {subItem?.fields?.["Link Text"]?.value}
                  </h2>
                  <ul className="flex flex-col items-start gap-1">
                    {visibleInnerItems.map((childSubItem: any) => (
                      <li
                        key={childSubItem?.id}
                        className="rounded-lg w-full font-medium text-sm xl:text-base whitespace-nowrap hover:bg-neutral-100 hover:underline transition-colors"
                      >
                        <a
                          href={
                            CleanRelativeLink(childSubItem?.fields?.Link?.value?.href) +
                            (childSubItem?.fields?.Link?.value?.querystring &&
                              childSubItem?.fields?.Link?.value?.querystring !== "undefined" &&
                              childSubItem?.fields?.Link?.value?.querystring !== "null"
                              ? childSubItem?.fields?.Link?.value?.querystring
                              : "")
                          }
                          target={childSubItem?.fields?.Link?.value?.target || "_self"}
                          className="py-2 px-3 xl:px-4 w-full flex gap-1 items-center"
                        >
                          {childSubItem?.fields?.["Link Text"]?.value}{" "}
                          {childSubItem?.fields?.Link?.value?.linktype === "external" && (
                            <div>
                              <ExternalLinkTypeIcon />
                            </div>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <a
                  key={subItem?.id}
                  href={
                    CleanRelativeLink(subItem?.fields?.Link?.value?.href) +
                    (subItem?.fields?.Link?.value?.querystring &&
                      subItem?.fields?.Link?.value?.querystring !== "undefined" &&
                      subItem?.fields?.Link?.value?.querystring !== "null"
                      ? subItem?.fields?.Link?.value?.querystring
                      : "")
                  }
                  target={subItem?.fields?.Link?.value?.target || "_self"}
                  className="flex gap-1 items-center rounded-lg py-2 px-3 xl:px-4 min-w-[154px] w-full h-fit font-medium text-sm xl:text-base whitespace-nowrap hover:bg-neutral-100 hover:underline transition-colors"
                >
                  {subItem?.fields?.["Link Text"]?.value}{" "}
                  {subItem?.fields?.Link?.value?.linktype === "external" && (
                    <div>
                      <ExternalLinkTypeIcon />
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        )}
      </div>

      {/* search panel */}
      <HeaderSearchPanel
        SearchButtonText={props?.fields?.SearchButtonText?.value}
        SearchInputPlaceholder={props?.fields?.SearchInputPlaceholder?.value}
        SuggestionsHeader={props?.fields?.SuggestionsHeader?.value}
        SuggestionItems={props?.fields?.SuggestionItems}
        isSearchPanelOpen={!subMenuOpen && isSearchPanelOpen}
        setIsSearchPanelOpen={setIsSearchPanelOpen}
        locale={props?.locale}
      />
    </>
  );
};

export default DesktopNav;
